import { Component, OnInit, OnChanges, SimpleChanges, Input, Output, EventEmitter} from '@angular/core';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { each, remove, find, toLower, filter } from 'lodash';

import { Execution } from './execution';
import { Dossier } from '../dossier/dossier';
import { DossierModel } from '../dossier-model/dossier-model';
import { RecordLink } from '../record-link/record-link';
import { Document } from '../document/document';


type TreeItemType = 'ROOT' | 'LINK_GROUP' | 'RECORD';

class TreeItem {
  label: string
  type: TreeItemType
  data: any
  items: TreeItem[]
  parent: TreeItem
  expanded: boolean = false;
  documents: Document[]

  sort() {
    if (this.type === 'ROOT') {
      each(this.items, item => item.sort());
    }
    else if (this.type === 'LINK_GROUP') {
      this.items.sort((a, b) => {
        let labelA = toLower(a.label);
        let labelB = toLower(b.label);

        if (labelA === labelB) {
          return 0;
        }
        else if (labelA < labelB) {
          return -1;
        }
        else {
          return 1;
        }
      });
    }
  }

  updateLabels() {
    if (this.type === 'ROOT') {
      if(this.data) {
        this.label = this.data.model.label;
      }
    }
    else if (this.type === 'LINK_GROUP') {
      this.label = this.data.text;
    }
    else if (this.type === 'RECORD') {
      this.label = this.data.record.name;
    }

    each(this.items, item => item.updateLabels());
  }

  deleteEmptyLinkGroups() {
    remove(this.items, (group) => {
      return !group.items || group.items.length === 0;
    });
  }
}


@Component({
  selector: 't-download-tree',
  templateUrl: './download-tree.component.html',
})
export class DownloadTreeComponent implements OnInit {

  @Input() execution: Execution
  @Output() downloaded = new EventEmitter();

  
  dossier: Dossier
  dossierModel: DossierModel
  root: TreeItem

  constructor() {
  }

  ngOnInit() {
    this.dossier = this.execution.dossiers[0].dossier;
    this.dossierModel = this.execution.dossier_model;

    // Attach models on each link
    each(this.execution.links, link => {
      let target = find(this.dossierModel.targets, target => target.id === link.name);

      if (target) {
        link.model = target.link;
      }
    });

    // Detect root item
    each(this.execution.links, link => {
      if (link.model.is_root) {
        let item = new TreeItem();
        item.type = 'ROOT';
        item.data = link;
        item.items = [];
        item.expanded = true;

        this.addDocuments(item);

        this.root = item;
      }
    });

    // Add a placeholder root if a link was not supplied
    if (!this.root) {
      let item = new TreeItem();
        item.type = 'ROOT';
        item.items = [];
        item.expanded = true;

        let rootLinkModel = find(this.dossierModel.data, (linkModel: any) => linkModel.is_root);

        item.label = rootLinkModel.label;

        this.root = item;
    }

    // Build sub links
    each(this.dossierModel.targets, target => {
      // Ignore root link
      if (target.link.is_root) {
        return;
      }

      let item = new TreeItem();
      item.type = 'LINK_GROUP';
      item.data = target;
      item.items = [];
      item.expanded = true;

      this.root.items.push(item);

      this.addLinks(target.id, item);
    });

    this.root.deleteEmptyLinkGroups();
    this.root.updateLabels();
    this.root.sort();
  }

  private addLinks(linkName: string, parentItem: TreeItem) {
    each(this.execution.links, link => {
      // Ignore links with different name
      if (link.name !== linkName) {
        return;
      }

      this.addLink(link, parentItem);
    });
  }

  private addLink(link: RecordLink, parentItem: TreeItem) {
    let item = new TreeItem();
    item.type = 'RECORD';
    item.data = link;
    item.parent = parentItem;
    item.expanded = true;

    this.addDocuments(item);

    parentItem.items.push(item);
  }

  private addDocuments(item: TreeItem) {
    let link: RecordLink = item.data;

    item.documents = filter(this.execution.revision.documents, doc => doc.target === link.name);
  }

  expand(item: TreeItem) {
    item.expanded = !item.expanded;

    return false;
  }

  onDownload(item: TreeItem, document: Document) {
    
    this.downloaded.emit({
      link: item.data,
      document: document
    });

    return false;
  }
}